// What each side card of a task group's page says in its folded header (S136), as pure
// functions of the data the card already fetched: the cards render these with ui/summary.
import { type CostReport, dailySeries } from "../../costs/model";
import type { Tone } from "../../ui/tones";
import { REQUIREMENT_TONE } from "../display";
import type { GroupRequirements } from "../model";
import type { SharedFiles } from "./sharedFiles";

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export interface ResourcesSummary {
  cpus: number;
  gib: number;
  /** The size as a share of the owner's limit, 0–100 each. */
  cpuPct: number;
  memPct: number;
  /** A resize still under way: restarting now, or after the current task. */
  busy: "restarting" | "pending" | null;
}

export function resourcesSummary(
  env: { limits_cpu: number; limits_memory_bytes: number; limits_state: string },
  limit: { cpus: number; memory_bytes: number },
): ResourcesSummary {
  const busy = env.limits_state === "restarting" || env.limits_state === "pending" ? env.limits_state : null;
  return {
    cpus: env.limits_cpu,
    gib: Math.floor(env.limits_memory_bytes / 1024 ** 3),
    cpuPct: pct(env.limits_cpu, limit.cpus),
    memPct: pct(env.limits_memory_bytes, limit.memory_bytes),
    busy,
  };
}

/** Blocked requests are the news: red when any, grey otherwise. */
export const blockedTone = (blocked: number): Tone => (blocked > 0 ? "red" : "gray");

/** How many days the cost sparkline covers, ending today. */
export const TREND_DAYS = 14;

export interface CostsSummary {
  total: number;
  today: number;
  /** Spend per UTC day for the last TREND_DAYS days, oldest first; empty when nothing was spent. */
  trend: number[];
}

export function costsSummary(report: CostReport, now: number): CostsSummary {
  const days = dailySeries(report, now).map((d) => d.org + d.platform);
  return {
    total: report.total_usd,
    today: days.at(-1) ?? 0,
    trend: days.length > 0 ? padStart(days.slice(-TREND_DAYS), TREND_DAYS) : [],
  };
}

const padStart = (values: number[], n: number) => [...Array<number>(Math.max(0, n - values.length)).fill(0), ...values];

export interface RequirementsSummary {
  total: number;
  /** Requirements that do not stop a task (the API's own `satisfied`). */
  ready: number;
  tones: Tone[];
  tone: Tone;
}

export function requirementsSummary(data: GroupRequirements): RequirementsSummary {
  return {
    total: data.requirements.length,
    ready: data.requirements.filter((r) => r.satisfied).length,
    tones: data.requirements.map((r) => REQUIREMENT_TONE[r.state]),
    tone: data.satisfied ? "green" : "red",
  };
}

export interface SharedSummary {
  files: number;
  bytes: number;
  /** The listing stopped at its limit: there are more files than counted. */
  more: boolean;
}

export function sharedSummary(listing: SharedFiles): SharedSummary {
  const files = listing.entries.filter((e) => !e.dir);
  return { files: files.length, bytes: files.reduce((sum, e) => sum + e.size, 0), more: listing.truncated };
}
